import { forwardRef } from 'react';

import {
  faChevronDown,
  faPaperPlane,
  faPlus,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styled from 'styled-components';

import AuthorizedButton from 'components/inputs/AuthorizedButton';
import CreateCaseModal from 'components/CreateCaseModal';
import DatePicker from 'components/inputs/DatePicker';
import { AssignmentViewModel, CaseTypeEnum } from 'api/model';
import { InternalDeliveryGroupAccessPolicy } from 'constants/Roles';
import AssignmentCountWidget from 'components/AssignmentsList/AssignmentCountWidget';
import { useAssignmentsFilters } from 'contexts/assignmentsFilters/useAssignmentsFilters';
import FilterPicker from 'components/filterBar/FilterPicker';
import FilterSearchInput from 'components/filterBar/FilterSearchInput';
import { formatDate } from 'utils/date-helpers';
import useModalStack from 'contexts/modal/useModalStack';

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: baseline;
  margin: 10px 10px;
  gap: 5px;
`;

const MyLabel = styled.label`
  margin-right: 5px;
`;

const DatePickerButton = styled.button`
  display: flex;
  align-items: center;
  padding: 0 10px;
  height: 30px;

  color: ${({ theme }) => theme.colors.foreground.tint};
  background-color: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: ${({ theme }) => theme.sizes.radius.input}px;
  font: inherit;

  div {
    white-space: nowrap;
  }

  span {
    display: block;
    margin-left: auto;
    padding-left: 10px;
    font-size: 0.5em;
    color: ${({ theme }) => theme.colors.foreground.primary};
  }
`;

const DatePickerContainer = styled.div``;

const StyledAuthorizedButton = styled(AuthorizedButton)`
  height: 30px;
  padding: 0 10px;
  margin-right: 10px;
  font: inherit;
  white-space: nowrap;

  & > *:first-child {
    margin-right: 6px;
    font-size: 12px;
  }
`;

const MyFilterSearchInput = styled(FilterSearchInput)`
  margin-left: auto;
`;

const CustomInput = forwardRef<HTMLButtonElement, { date: Date }>(
  ({ date, ...props }, ref) => (
    <DatePickerButton ref={ref} type="button" {...props}>
      <div>{formatDate(date)}</div>
      <span>
        <FontAwesomeIcon icon={faChevronDown} />
      </span>
    </DatePickerButton>
  )
);

interface Props {
  onSendToFieldTestersClick?: () => void;
  assignments: AssignmentViewModel[];
  onUpdate?: () => void;
}

const TestDrivesFilterBar: React.FC<Props> = ({
  assignments,
  onSendToFieldTestersClick,
  onUpdate,
}) => {
  const modalStack = useModalStack();
  const {
    state: { date },
    setDate,
  } = useAssignmentsFilters();

  const handleChangeDate = (
    newDate: Date | [Date | null, Date | null] | null
  ) => {
    if (newDate) {
      if (!Array.isArray(newDate)) {
        setDate(newDate);
      }
    }
  };

  const handleCaseCreated = () => {
    modalStack.pop();
    onUpdate?.();
  };

  return (
    <Wrapper>
      <MyLabel>Visa dag:</MyLabel>
      <DatePickerContainer>
        <DatePicker
          calendarStartDay={1}
          customInput={<CustomInput date={date} />}
          dateFormat="yyyy-MM-dd"
          locale="sv"
          onChange={(d) => handleChangeDate(d)}
          populateHolidays
          selected={date}
        />
      </DatePickerContainer>
      <AssignmentCountWidget assignments={assignments} />
      <FilterPicker />
      <StyledAuthorizedButton
        onClick={onSendToFieldTestersClick}
        roles={InternalDeliveryGroupAccessPolicy.allow}
      >
        <FontAwesomeIcon icon={faPaperPlane} />
        Skicka uppdrag till fälttestare
      </StyledAuthorizedButton>

      <StyledAuthorizedButton
        onClick={() => {
          modalStack.push(
            <CreateCaseModal
              caseType={CaseTypeEnum.TestDrive}
              onCaseCreated={handleCaseCreated}
              onClose={() => modalStack.pop()}
            />
          );
        }}
        roles={InternalDeliveryGroupAccessPolicy.allow}
      >
        <FontAwesomeIcon icon={faPlus} />
        Lägg till
      </StyledAuthorizedButton>

      <MyFilterSearchInput />
    </Wrapper>
  );
};

export default TestDrivesFilterBar;
